import { useEffect, useRef, useState } from "react";
import Modal from "@modalSrc/Modal";
import ModalLanguageSelector from "@modalSrc/ModalLanguageSelector";
import RightTimeApi from "@/API";
import { toast } from "react-toastify";
import ConfirmModal from "@elements/modals/ConfirmModal";
import ReportViewModal from "@elements/modals/ReportViewModal";
import { downloadResponseAsFile } from "@/utils/authenticatedFileDownload";

/** API returns template HTML as base64 (UTF-8); editor uses plain text. */
function decodeReportHtmlFromApi(raw: string): string {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  try {
    const bin = atob(s);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new TextDecoder().decode(bytes);
  } catch {
    // Older API returned raw HTML in JSON
    return s;
  }
}

function encodeReportHtmlForApi(plain: string): string {
  const bytes = new TextEncoder().encode(plain);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin);
}

function normalizeIntIds(raw: unknown): number[] {
  if (!Array.isArray(raw)) return [];
  const out: number[] = [];
  for (const x of raw) {
    const n = typeof x === "number" && Number.isFinite(x) ? x : Number(x);
    if (Number.isFinite(n) && n > 0) out.push(Math.trunc(n));
  }
  return [...new Set(out)];
}

type ReportDefinitionModalProps = {
  reportDefinitionId: number | null;
  onClose: (refresh: boolean) => void;
  updateSpinner?: (show: boolean) => void;
};

type DefinitionState = {
  id: number;
  name: string;
  active: boolean;
  testTypeIds: number[];
  /** CTT rows linked to this report (persisted via API). */
  companyTestTypeIds: number[];
};

type SupportedLang = { id: number; name: string; abbrev?: string };

type TestTypeOption = {
  id: number;
  name: string;
  ctts: {
    companyId: number;
    companyName: string;
    cttId: number;
    ctt: {
      displayName: string;
    };
  }[];
};

export default function ReportDefinitionModal({ reportDefinitionId, onClose, updateSpinner }: ReportDefinitionModalProps) {
  const [definition, setDefinition] = useState<DefinitionState>({
    id: 0,
    name: "",
    active: true,
    testTypeIds: [],
    companyTestTypeIds: [],
  });
  const [testTypeOptions, setTestTypeOptions] = useState<TestTypeOption[]>([]);
  const [supportedLanguages, setSupportedLanguages] = useState<SupportedLang[]>([]);
  const [selectedLanguageId, setSelectedLanguageId] = useState<number | null>(null);
  /** Template HTML per system language id (editor + save). */
  const [htmlByLanguageId, setHtmlByLanguageId] = useState<Record<number, string>>({});
  /** Last saved template per language (for dirty detection). */
  const [baselineHtmlByLanguageId, setBaselineHtmlByLanguageId] = useState<Record<number, string>>({});
  /** ReportLanguage row id when persisted; used for preview/PDF targeting. */
  const [reportLanguageRowIdByLanguageId, setReportLanguageRowIdByLanguageId] = useState<Record<number, number>>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [templateParams, setTemplateParams] = useState<{
    staticParameters: string[];
    testFieldParameters: string[];
    gradingParameters: string[];
  } | null>(null);
  const [selectedTestTypeId, setSelectedTestTypeId] = useState<number | null>(null);
  const [selectedCttId, setSelectedCttId] = useState<number | null>(null);
  /** Placeholder values for preview only; keys match API tokens (e.g. FirstName, testField:Label, grading:x). */
  const [parameterOverrides, setParameterOverrides] = useState<Record<string, string>>({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isOpen = reportDefinitionId !== null;
  const isNew = Number(reportDefinitionId || 0) <= 0;

  useEffect(() => {
    if (!isOpen) return;
    loadTestTypesAsync();

    if (isNew) {
      setDefinition({ id: 0, name: "", active: true, testTypeIds: [], companyTestTypeIds: [] });
      setSupportedLanguages([]);
      setSelectedLanguageId(null);
      setHtmlByLanguageId({});
      setBaselineHtmlByLanguageId({});
      setReportLanguageRowIdByLanguageId({});
      setSelectedTestTypeId(null);
      setSelectedCttId(null);
      setTemplateParams(null);
      setParameterOverrides({});
      setPreviewOpen(false);
      setPreviewHtml("");
      void loadSupportedLanguagesForNewAsync();
      return;
    }

    loadDefinitionAsync(reportDefinitionId as number);
  }, [isOpen, reportDefinitionId]);

  useEffect(() => {
    setParameterOverrides({});
  }, [selectedCttId]);

  /** For existing definitions: select an assigned test type row and a CTT row once options + assignments are loaded. */
  useEffect(() => {
    if (!isOpen || isNew) return;
    if (!definition.id || testTypeOptions.length === 0) return;

    const assigned = normalizeIntIds(definition.testTypeIds).filter((id) =>
      testTypeOptions.some((tt) => tt.id === id)
    );

    if (assigned.length === 0) {
      setSelectedTestTypeId(null);
      setSelectedCttId(null);
      setTemplateParams(null);
      return;
    }

    setSelectedTestTypeId((prev) =>
      prev != null && assigned.includes(prev) ? prev : assigned[0]!
    );
  }, [isOpen, isNew, definition.id, definition.testTypeIds, testTypeOptions]);

  /** Keep a company test type selected for the current test type (prefer associated CTT, then prior row). */
  useEffect(() => {
    if (!isOpen) return;
    if (selectedTestTypeId == null) return;

    const tt = testTypeOptions.find((t) => t.id === selectedTestTypeId);
    if (!tt?.ctts?.length) {
      setSelectedCttId(null);
      setTemplateParams(null);
      return;
    }

    const associatedFirst = tt.ctts.find((c) => definition.companyTestTypeIds.includes(c.cttId))?.cttId;

    setSelectedCttId((prev) => {
      if (prev != null && tt.ctts.some((c) => c.cttId === prev)) return prev;
      if (associatedFirst != null) return associatedFirst;
      return tt.ctts[0]!.cttId;
    });
  }, [isOpen, selectedTestTypeId, testTypeOptions, definition.companyTestTypeIds]);

  /** Load template parameters when a CTT row is selected (including auto-select on open). */
  useEffect(() => {
    if (!isOpen) return;
    if (!selectedCttId || selectedTestTypeId == null) return;
    loadTemplateParamsAsync(selectedCttId);
  }, [isOpen, selectedCttId, selectedTestTypeId]);

  const selectedTestType = testTypeOptions.find((tt) => tt.id === selectedTestTypeId) ?? null;

  async function loadTemplateParamsAsync(cttId: number | null) {
    if (!cttId || cttId <= 0 || selectedTestTypeId == null) {
      setTemplateParams(null);
      return;
    }
    const resp = await RightTimeApi.getAsync(`/ReportDefinition/Parameters/${cttId}`);
    if (resp?.ok && resp.jsonData) {
      setTemplateParams({
        staticParameters: Array.isArray(resp.jsonData.staticParameters) ? resp.jsonData.staticParameters : [],
        testFieldParameters: Array.isArray(resp.jsonData.testFieldParameters) ? resp.jsonData.testFieldParameters : [],
        gradingParameters: Array.isArray(resp.jsonData.gradingParameters) ? resp.jsonData.gradingParameters : [],
      });
    } else {
      setTemplateParams(null);
    }
  }

  function setOverrideForParam(paramKey: string, value: string) {
    setParameterOverrides((prev) => ({ ...prev, [paramKey]: value }));
  }

  function buildPreviewOverridesPayload(params: NonNullable<typeof templateParams>): Record<string, string> {
    const keys = [
      ...params.staticParameters,
      ...params.testFieldParameters,
      ...params.gradingParameters,
    ];
    const out: Record<string, string> = {};
    for (const k of keys) {
      const v = parameterOverrides[k];
      if (v !== undefined && String(v).trim() !== "") out[k] = v;
    }
    return out;
  }

  function currentTemplateHtml(): string {
    if (selectedLanguageId == null) return "";
    return htmlByLanguageId[selectedLanguageId] ?? "";
  }

  async function previewReportWithOverridesAsync() {
    if (!definition.id) {
      toast.info("Save the report definition before preview.");
      return;
    }
    if (!selectedCttId || !templateParams) {
      toast.info("Select a company test type to load parameters, then preview.");
      return;
    }
    if (!currentTemplateHtml().trim()) {
      toast.error("Add template HTML before preview.");
      return;
    }

    const rowId =
      selectedLanguageId != null ? reportLanguageRowIdByLanguageId[selectedLanguageId] ?? 0 : 0;

    updateSpinner?.(true);
    try {
      const body: Record<string, unknown> = {
        companyTestTypeId: selectedCttId,
        overrides: buildPreviewOverridesPayload(templateParams),
        htmlTemplate: currentTemplateHtml(),
      };
      if (rowId > 0 && Number.isFinite(rowId)) {
        body.reportLanguageId = rowId;
      }
      const resp = await RightTimeApi.postAsync(`/ReportDefinition/${definition.id}/Preview`, body);
      if (!resp?.ok || !resp.jsonData) {
        toast.error("Preview failed.");
        return;
      }
      const jd = resp.jsonData as Record<string, unknown>;
      const html = typeof jd === "string" ? jd : String(jd.html ?? jd.Html ?? "");
      if (!html.trim()) {
        toast.warn("Preview returned no HTML. Check the template or save a non-empty template for this language.");
        return;
      }
      setPreviewHtml(html);
      setPreviewOpen(true);
    } finally {
      updateSpinner?.(false);
    }
  }

  async function downloadReportPdfWithOverridesAsync() {
    if (!definition.id) {
      toast.info("Save the report definition before generating a PDF.");
      return;
    }
    if (!selectedCttId || !templateParams) {
      toast.info("Select a company test type to load parameters, then render PDF.");
      return;
    }
    if (!currentTemplateHtml().trim()) {
      toast.error("Add template HTML before generating a PDF.");
      return;
    }

    const rowId =
      selectedLanguageId != null ? reportLanguageRowIdByLanguageId[selectedLanguageId] ?? 0 : 0;

    updateSpinner?.(true);
    try {
      const body: Record<string, unknown> = {
        companyTestTypeId: selectedCttId,
        overrides: buildPreviewOverridesPayload(templateParams),
        htmlTemplate: currentTemplateHtml(),
      };
      if (rowId > 0 && Number.isFinite(rowId)) {
        body.reportLanguageId = rowId;
      }
      const resp = await RightTimeApi.postAsync(`/ReportDefinition/${definition.id}/Pdf`, body);
      const safeName =
        (definition.name || "report").replace(/[^a-z0-9_-]+/gi, "_").replace(/^_|_$/g, "") || "report";
      const ok = await downloadResponseAsFile(resp, `${safeName}_${definition.id}.pdf`);
      if (!ok) toast.error("PDF generation failed.");
      else toast.success("PDF downloaded.");
    } finally {
      updateSpinner?.(false);
    }
  }

  function insertParam(param: string) {
    const ta = textareaRef.current;
    const lid = selectedLanguageId;
    if (!ta || lid == null) return;
    const cur = htmlByLanguageId[lid] ?? "";
    const start = ta.selectionStart ?? cur.length;
    const end = ta.selectionEnd ?? cur.length;
    const token = `[${param}]`;
    const newVal = cur.slice(0, start) + token + cur.slice(end);
    setHtmlByLanguageId((prev) => ({ ...prev, [lid]: newVal }));
    setTimeout(() => {
      ta.focus();
      const pos = start + token.length;
      ta.setSelectionRange(pos, pos);
    }, 0);
  }

  async function loadTestTypesAsync() {
    const resp = await RightTimeApi.getAsync("/TestTypes/Simple");
    if (resp?.ok && Array.isArray(resp.jsonData)) {
      setTestTypeOptions(resp.jsonData.map((t: any) => ({
        id: t.id,
        name: t.name,
        ctts: Array.isArray(t.ctts) ? t.ctts : [],
      })));
    }
  }

  function normalizeSupportedLangs(raw: unknown): SupportedLang[] {
    if (!Array.isArray(raw)) return [];
    const out: SupportedLang[] = [];
    for (const x of raw) {
      const id = Number((x as any).id ?? (x as any).Id);
      if (!Number.isFinite(id) || id <= 0) continue;
      const name = String((x as any).name ?? (x as any).Name ?? "").trim();
      if (!name) continue;
      const abbrev = (x as any).abbrev ?? (x as any).Abbrev;
      out.push({ id, name, abbrev: abbrev != null ? String(abbrev) : undefined });
    }
    return out;
  }

  async function loadSupportedLanguagesForNewAsync() {
    const resp = await RightTimeApi.getAsync("/Languages");
    if (!resp?.ok || !Array.isArray(resp.jsonData)) {
      toast.error("Failed to load supported languages.");
      return;
    }
    const langs = normalizeSupportedLangs(resp.jsonData);
    setSupportedLanguages(langs);
    const defaultId = langs.find((l) => l.id === 1)?.id ?? langs[0]?.id ?? null;
    setSelectedLanguageId(defaultId);
    const init: Record<number, string> = {};
    for (const l of langs) init[l.id] = "";
    setHtmlByLanguageId(init);
    setBaselineHtmlByLanguageId({ ...init });
    setReportLanguageRowIdByLanguageId({});
  }

  function selectTestTypeRow(tt: TestTypeOption) {
    setSelectedTestTypeId(tt.id);
    setSelectedCttId(null);
    setTemplateParams(null);
  }

  function selectCttRow(cttId: number) {
    setSelectedCttId(cttId);
  }

  function cttIdsForTestTypeId(testTypeId: number): number[] {
    const tt = testTypeOptions.find((t) => t.id === testTypeId);
    if (!tt?.ctts?.length) return [];
    return tt.ctts.map((c) => c.cttId);
  }

  function toggleCttForReport(cttId: number, testTypeId: number, checked: boolean) {
    setDefinition((d) => {
      const tt = testTypeOptions.find((t) => t.id === testTypeId);
      let nextCtts = [...d.companyTestTypeIds];
      let nextTt = [...d.testTypeIds];

      if (checked) {
        nextCtts = normalizeIntIds([...nextCtts, cttId]);
        if (!nextTt.includes(testTypeId)) nextTt = normalizeIntIds([...nextTt, testTypeId]);
      } else {
        nextCtts = nextCtts.filter((id) => id !== cttId);
        const anyOther =
          tt?.ctts.some((c) => c.cttId !== cttId && nextCtts.includes(c.cttId)) ?? false;
        if (!anyOther) nextTt = nextTt.filter((id) => id !== testTypeId);
      }

      return { ...d, testTypeIds: nextTt, companyTestTypeIds: nextCtts };
    });
  }

  function toggleTestTypeAssigned(testTypeId: number, checked: boolean) {
    setDefinition((d) => {
      const strip = cttIdsForTestTypeId(testTypeId);
      const nextTt = checked
        ? normalizeIntIds([...d.testTypeIds, testTypeId])
        : d.testTypeIds.filter((id) => id !== testTypeId);
      const nextCtts = checked
        ? d.companyTestTypeIds
        : d.companyTestTypeIds.filter((cid) => !strip.includes(cid));
      return { ...d, testTypeIds: nextTt, companyTestTypeIds: nextCtts };
    });
  }

  async function loadDefinitionAsync(id: number) {
    updateSpinner?.(true);
    try {
      const resp = await RightTimeApi.getAsync(`/ReportDefinition/${id}`);
      if (!resp?.ok || !resp?.jsonData) {
        toast.error("Failed to load report definition.");
        return;
      }

      setDefinition({
        id: resp.jsonData.id || resp.jsonData.Id || 0,
        name: resp.jsonData.name || resp.jsonData.Name || "",
        active: resp.jsonData.active !== false && resp.jsonData.Active !== false,
        testTypeIds: normalizeIntIds(
          Array.isArray(resp.jsonData.testTypeIds)
            ? resp.jsonData.testTypeIds
            : Array.isArray(resp.jsonData.TestTypeIds)
              ? resp.jsonData.TestTypeIds
              : []
        ),
        companyTestTypeIds: normalizeIntIds(
          Array.isArray(resp.jsonData.companyTestTypeIds)
            ? resp.jsonData.companyTestTypeIds
            : Array.isArray(resp.jsonData.CompanyTestTypeIds)
              ? resp.jsonData.CompanyTestTypeIds
              : []
        ),
      });

      let supported = normalizeSupportedLangs(
        resp.jsonData.supportedLanguages ?? resp.jsonData.SupportedLanguages
      );
      if (supported.length === 0) {
        const langResp = await RightTimeApi.getAsync("/Languages");
        if (langResp?.ok && Array.isArray(langResp.jsonData)) {
          supported = normalizeSupportedLangs(langResp.jsonData);
        }
      }
      setSupportedLanguages(supported);

      const langRows = Array.isArray(resp.jsonData.languages)
        ? resp.jsonData.languages
        : Array.isArray(resp.jsonData.Languages)
          ? resp.jsonData.Languages
          : [];

      const htmlMap: Record<number, string> = {};
      const baselineMap: Record<number, string> = {};
      const rowIdMap: Record<number, number> = {};
      for (const s of supported) {
        htmlMap[s.id] = "";
        baselineMap[s.id] = "";
      }
      for (const row of langRows) {
        const r = row as Record<string, unknown>;
        const languageId = Number(r.languageId ?? r.LanguageId);
        if (!Number.isFinite(languageId) || languageId <= 0) continue;
        const rowId = Number(r.id ?? r.Id);
        const wire = String(r.htmlTemplate ?? r.HtmlTemplate ?? "");
        const decoded = decodeReportHtmlFromApi(wire);
        htmlMap[languageId] = decoded;
        baselineMap[languageId] = decoded;
        if (Number.isFinite(rowId) && rowId > 0) rowIdMap[languageId] = rowId;
      }
      setHtmlByLanguageId(htmlMap);
      setBaselineHtmlByLanguageId({ ...baselineMap });
      setReportLanguageRowIdByLanguageId(rowIdMap);

      const defaultLangId = supported.find((s) => s.id === 1)?.id ?? supported[0]?.id ?? null;
      setSelectedLanguageId(defaultLangId);
    } finally {
      updateSpinner?.(false);
    }
  }

  async function saveDefinitionAsync() {
    if (!definition.name.trim()) {
      toast.error("Name is required.");
      return;
    }

    const hasAnyTemplate = supportedLanguages.some((s) => (htmlByLanguageId[s.id] ?? "").trim().length > 0);
    if (isNew && !hasAnyTemplate) {
      toast.error("Add HTML template for at least one supported language.");
      return;
    }

    updateSpinner?.(true);
    try {
      const payload = {
        id: definition.id,
        name: definition.name,
        active: definition.active,
        testTypeIds: definition.testTypeIds,
        companyTestTypeIds: definition.companyTestTypeIds,
      };

      const resp = isNew
        ? await RightTimeApi.postAsync("/ReportDefinition", payload)
        : await RightTimeApi.putAsync("/ReportDefinition", payload);

      if (!resp?.ok) {
        toast.error("Failed to save report definition.");
        return;
      }

      const createdOrExistingId = isNew
        ? Number(resp.jsonData?.id ?? resp.jsonData?.Id ?? 0)
        : definition.id;

      if (!createdOrExistingId) {
        toast.error("Report definition saved, but the server did not return an id.");
        onClose(true);
        return;
      }

      const postLanguage = async (languageId: number, plain: string) =>
        RightTimeApi.postAsync(`/ReportDefinition/${createdOrExistingId}/Language`, {
          languageId,
          htmlTemplate: encodeReportHtmlForApi(plain),
        });

      if (isNew) {
        for (const s of supportedLanguages) {
          const plain = htmlByLanguageId[s.id] ?? "";
          if (!plain.trim()) continue;
          const langResp = await postLanguage(s.id, plain);
          if (!langResp?.ok) {
            toast.error(`Report saved, but failed to save template for ${s.name}.`);
            onClose(true);
            return;
          }
        }
      } else {
        for (const s of supportedLanguages) {
          const plain = htmlByLanguageId[s.id] ?? "";
          const baseline = baselineHtmlByLanguageId[s.id] ?? "";
          if (plain === baseline) continue;
          const langResp = await postLanguage(s.id, plain);
          if (!langResp?.ok) {
            toast.error(`Metadata saved, but failed to save template for ${s.name}.`);
            return;
          }
          const jd = langResp.jsonData as Record<string, unknown> | undefined;
          const newRowId = Number(jd?.id ?? jd?.Id ?? 0);
          const respLangId = Number(jd?.languageId ?? jd?.LanguageId ?? s.id);
          if (Number.isFinite(newRowId) && newRowId > 0 && Number.isFinite(respLangId) && respLangId > 0) {
            setReportLanguageRowIdByLanguageId((prev) => ({ ...prev, [respLangId]: newRowId }));
          }
        }
        setBaselineHtmlByLanguageId({ ...htmlByLanguageId });
      }

      toast.success("Report definition saved.");
      onClose(true);
    } finally {
      updateSpinner?.(false);
    }
  }

  async function deleteDefinitionAsync() {
    if (!definition.id) return;

    setShowDeleteConfirm(false);
    updateSpinner?.(true);
    try {
      const resp = await RightTimeApi.deleteAsync(`/ReportDefinition/${definition.id}`);
      if (!resp?.ok) {
        toast.error("Failed to delete report definition.");
        return;
      }

      toast.success("Report definition deleted.");
      onClose(true);
    } finally {
      updateSpinner?.(false);
    }
  }

  if (!isOpen) return null;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={() => onClose(false)}
        title={isNew ? "New Report Definition" : `Edit Report Definition #${definition.id}`}
        size="lg"
        className="reportDefinitionModal largeModal"
        footer={(
          <>
            <div className="footerLeft">
              {!isNew && (
                <button type="button" className="btn btn-danger" onClick={() => setShowDeleteConfirm(true)}>
                  Delete
                </button>
              )}
              <button
                type="button"
                className="btn btn-secondary"
                onClick={previewReportWithOverridesAsync}
                title="Render using the current language template and parameter values in the table"
              >
                Preview
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={downloadReportPdfWithOverridesAsync}
                title="Download PDF using the same context as Preview (editor HTML, CTT, overrides)"
              >
                Render PDF
              </button>
            </div>
            <div className="footerCenter">
              {supportedLanguages.length > 0 ? (
                <ModalLanguageSelector
                  selectId="reportDefinitionLanguageSelect"
                  languages={supportedLanguages}
                  value={selectedLanguageId ?? ""}
                  onChange={(languageId) => {
                    const parsed = parseInt(languageId, 10);
                    if (parsed > 0) setSelectedLanguageId(parsed);
                  }}
                  placement="footer"
                  title="Select language template to edit"
                />
              ) : (
                <span className="reportFooterLangPlaceholder muted">Loading languages…</span>
              )}
            </div>
            <div className="footerRight">
              <button type="button" className="btn btn-secondary" onClick={() => onClose(false)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={saveDefinitionAsync}>
                Save
              </button>
            </div>
          </>
        )}
      >
        <div className="topSection">
          <div className="metaColumn">
            <div className="formGroup inlineLabel">
              <label htmlFor="reportDefName">Name</label>
              <input
                id="reportDefName"
                type="text"
                value={definition.name}
                onChange={(e) => setDefinition((d) => ({ ...d, name: e.target.value }))}
              />
            </div>

            <div className="formGroup inlineLabel">
              <label htmlFor="reportDefActive">Active</label>
              <input
                id="reportDefActive"
                type="checkbox"
                checked={definition.active}
                onChange={(e) => setDefinition((d) => ({ ...d, active: e.target.checked }))}
              />
            </div>

          </div>

          <div className="testTypesColumn">
            <div className="tableWrapper dimensionTable scrollable reportTestTypesTable">
              <table>
                <caption>
                  <span className="title">Assign Test Types</span>
                </caption>
                <thead>
                  <tr>
                    <th>Test Type</th>
                    <th>Select</th>
                  </tr>
                </thead>
                <tbody>
                  {testTypeOptions.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="empty">
                        No test types available.
                      </td>
                    </tr>
                  ) : (
                    testTypeOptions.map((tt) => {
                      const hasLinkedCtt = tt.ctts?.some((c) =>
                        definition.companyTestTypeIds.includes(c.cttId)
                      );
                      const rowClass = [
                        selectedTestTypeId === tt.id ? "active" : "",
                        hasLinkedCtt ? "associated" : "",
                      ]
                        .filter(Boolean)
                        .join(" ");
                      return (
                        <tr key={tt.id} className={rowClass}>
                          <td
                            onClick={() => selectTestTypeRow(tt)}
                            title="Select this test type"
                          >
                            {tt.name}
                          </td>
                          <td
                            onClick={() =>
                              toggleTestTypeAssigned(tt.id, !definition.testTypeIds.includes(tt.id))
                            }
                          >
                            <input
                              type="checkbox"
                              checked={definition.testTypeIds.includes(tt.id)}
                              onChange={(e) => toggleTestTypeAssigned(tt.id, e.target.checked)}
                              onClick={(e) => e.stopPropagation()}
                              title="Assign test type; uncheck clears CTT selections for this type."
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="cttsColumn">
            <div className="tableWrapper dimensionTable scrollable reportCttsTable">
              <table>
                <caption>
                  <span className="title">Company Test Types</span>
                </caption>
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>CTT Display Name</th>
                    <th>Select</th>
                  </tr>
                </thead>
                <tbody>
                  {!selectedTestType ? (
                    <tr>
                      <td colSpan={3} className="empty">
                        Select a test type.
                      </td>
                    </tr>
                  ) : selectedTestType.ctts.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="empty">
                        No CTTs available for this test type.
                      </td>
                    </tr>
                  ) : (
                    selectedTestType.ctts.map((ctt) => {
                      const linked = definition.companyTestTypeIds.includes(ctt.cttId);
                      const rowClass = [
                        selectedCttId === ctt.cttId ? "active" : "",
                        linked ? "associated" : "",
                      ]
                        .filter(Boolean)
                        .join(" ");
                      return (
                        <tr
                          key={ctt.cttId}
                          className={rowClass}
                          onClick={() => selectCttRow(ctt.cttId)}
                        >
                          <td>{ctt.companyName}</td>
                          <td>{ctt.ctt.displayName}</td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={linked}
                              onChange={(e) =>
                                toggleCttForReport(ctt.cttId, selectedTestType.id, e.target.checked)
                              }
                              title="Associate this company test type with the report"
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="templateEditorSection">
          <div className="formGroup htmlTemplateGroup">
            <div className="fieldWrapper">
              <textarea
                id="reportLangHtml"
                ref={textareaRef}
                rows={isNew ? 8 : 6}
                value={currentTemplateHtml()}
                onChange={(e) => {
                  const lid = selectedLanguageId;
                  if (lid == null) return;
                  const v = e.target.value;
                  setHtmlByLanguageId((prev) => ({ ...prev, [lid]: v }));
                }}
                disabled={selectedLanguageId == null}
                placeholder={
                  selectedLanguageId == null
                    ? "Loading languages…"
                    : isNew
                      ? "Enter the HTML template for this language…"
                      : "Enter HTML template for the selected language…"
                }
              />
            </div>
          </div>

          <div className="parametersColumn">
            <div className="tableWrapper dimensionTable scrollable reportParamsTable">
              <table>
                <caption>
                  <span className="title">Template Parameters</span>
                </caption>
                <thead>
                  <tr>
                    <th>Parameter</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {!templateParams ? (
                    <tr>
                      <td colSpan={2} className="empty">
                        Select both a test type and a company test type.
                      </td>
                    </tr>
                  ) : (
                    <>
                      {templateParams.staticParameters.map((p) => (
                        <tr key={p} className="paramRow">
                          <td className="paramNameCell" onClick={() => insertParam(p)} title={`Insert [${p}]`}>
                            [{p}]
                          </td>
                          <td className="paramValueCell" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="text"
                              value={parameterOverrides[p] ?? ""}
                              onChange={(e) => setOverrideForParam(p, e.target.value)}
                              placeholder="Optional"
                              aria-label={`Value for ${p}`}
                            />
                          </td>
                        </tr>
                      ))}
                      {templateParams.testFieldParameters.length > 0 && (
                        <>
                          <tr className="paramSectionHeader">
                            <td colSpan={2}>— Test Fields —</td>
                          </tr>
                          {templateParams.testFieldParameters.map((p) => (
                            <tr key={p} className="paramRow">
                              <td className="paramNameCell" onClick={() => insertParam(p)} title={`Insert [${p}]`}>
                                [{p}]
                              </td>
                              <td className="paramValueCell" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="text"
                                  value={parameterOverrides[p] ?? ""}
                                  onChange={(e) => setOverrideForParam(p, e.target.value)}
                                  placeholder="Optional"
                                  aria-label={`Value for ${p}`}
                                />
                              </td>
                            </tr>
                          ))}
                        </>
                      )}
                      {templateParams.gradingParameters.length > 0 && (
                        <>
                          <tr className="paramSectionHeader">
                            <td colSpan={2}>— Grading Values —</td>
                          </tr>
                          {templateParams.gradingParameters.map((p) => (
                            <tr key={p} className="paramRow">
                              <td className="paramNameCell" onClick={() => insertParam(p)} title={`Insert [${p}]`}>
                                [{p}]
                              </td>
                              <td className="paramValueCell" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="text"
                                  value={parameterOverrides[p] ?? ""}
                                  onChange={(e) => setOverrideForParam(p, e.target.value)}
                                  placeholder="Optional"
                                  aria-label={`Value for ${p}`}
                                />
                              </td>
                            </tr>
                          ))}
                        </>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onConfirm={deleteDefinitionAsync}
        onCancel={() => setShowDeleteConfirm(false)}
        title="Delete Report Definition"
        message={`Delete ${definition.name}?`}
      />

      <ReportViewModal
        isOpen={previewOpen}
        title={`Preview: ${definition.name || "Report"}`}
        html={previewHtml}
        onClose={() => setPreviewOpen(false)}
      />
    </>
  );
}
